/**
 * M7 S32: the Logs screen's words (`logsScreen.json`, Zod-validated). Every label the screen shows comes from here,
 * so the naming pass is a data edit.
 */
import { z } from 'zod'
import { CASE_LINES, LEAD_POI_KINDS, LOG_SOURCES } from '../../../story/types'
import configJson from '../logsScreen.json'

const text = z.string().min(1)
const columns = z.array(text).min(1)

function record<K extends string>(keys: readonly K[]): z.ZodType<Record<K, string>> {
  const shape = Object.fromEntries(keys.map((k) => [k, text])) as Record<K, typeof text>
  return z.strictObject(shape) as unknown as z.ZodType<Record<K, string>>
}

const TAB_SOURCES = LOG_SOURCES.filter((s) => s !== 'yourRecords')

export const logsConfigSchema = z.strictObject({
  comment: z.string(),
  title: text,
  tabs: z.strictObject({ case: text, records: text, messages: text }),
  sources: z.strictObject({
    order: z.array(z.enum(TAB_SOURCES as [string, ...string[]])),
    labels: record(LOG_SOURCES),
    guardianWreckEarly: text,
  }),
  unread: text,
  unreadTab: text,
  close: text,
  back: text,
  empty: z.strictObject({ source: text, leads: text, questions: text, records: text, screen: text }),
  caseLine: z.strictObject({ label: text, words: record(CASE_LINES), hints: record(CASE_LINES) }),
  leads: z.strictObject({
    heading: text,
    setNav: text,
    navSet: text,
    navUnavailable: text,
    poiKinds: record(LEAD_POI_KINDS),
    pointsTo: text,
  }),
  questions: z.strictObject({ heading: text }),
  records: z.strictObject({
    heading: text,
    sales: z.strictObject({ title: text, columns, signed: text }),
    kills: z.strictObject({
      title: text,
      columns,
      labelEarly: text,
      labelLate: text,
      selfDefence: text,
      unknownHull: text,
    }),
    meter: z.strictObject({ title: text, columns, reading: text }),
    corp: z.strictObject({ title: text, line: text }),
    handedOver: z.strictObject({ title: text, columns, recipient: text }),
    dayPrefix: text,
  }),
  tokenFallback: text,
})
export type LogsConfig = z.output<typeof logsConfigSchema>

export function loadLogsConfig(raw: unknown = configJson): LogsConfig {
  const cfg = logsConfigSchema.parse(raw)
  const seen = new Set<string>()
  for (const s of cfg.sources.order) {
    if (seen.has(s)) throw new Error(`logsScreen.json: sources.order lists '${s}' twice`)
    seen.add(s)
  }
  for (const s of TAB_SOURCES) if (!seen.has(s)) throw new Error(`logsScreen.json: sources.order is missing '${s}'`)
  return cfg
}
